const STORAGE_KEY = "judi_error_log";
const MAX_ENTRIES = 100;

const SENSITIVE_HEADERS = new Set([
  "authorization",
  "apikey",
  "x-api-key",
  "cookie",
  "set-cookie",
]);

export type ErrorType =
  | "network"
  | "console.error"
  | "console.warn"
  | "unhandled-rejection"
  | "global-error";

export interface ErrorEntry {
  id: string;
  type: ErrorType;
  timestamp: number;
  summary: string;
  fullReport: string;
}

type Subscriber = () => void;

let errors: ErrorEntry[] = [];
let subscribers: Subscriber[] = [];
let initialized = false;

function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(errors));
  } catch {
    // localStorage full or unavailable
  }
}

function notify() {
  for (const sub of subscribers) {
    try {
      sub();
    } catch {
      // ignore subscriber errors
    }
  }
}

function addError(entry: Omit<ErrorEntry, "id">) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  errors.push({ ...entry, id });
  if (errors.length > MAX_ENTRIES) {
    errors = errors.slice(errors.length - MAX_ENTRIES);
  }
  saveToStorage();
  notify();
}

function safeStringify(value: unknown): string {
  const seen = new WeakSet();
  return JSON.stringify(
    value,
    (_key, val) => {
      if (typeof val === "object" && val !== null) {
        if (seen.has(val)) return "[Circular]";
        seen.add(val);
      }
      if (val instanceof Error) {
        return {
          name: val.name,
          message: val.message,
          stack: val.stack,
        };
      }
      return val;
    },
    2,
  );
}

function serializeArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg instanceof Error)
        return `${arg.name}: ${arg.message}\n${arg.stack ?? ""}`;
      try {
        return safeStringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(" ");
}

function redactHeaders(headers: Record<string, string>): Record<string, string> {
  const redacted: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    redacted[key] = SENSITIVE_HEADERS.has(key.toLowerCase())
      ? "[REDACTED]"
      : value;
  }
  return redacted;
}

function headersToRecord(headers: Headers): Record<string, string> {
  const record: Record<string, string> = {};
  headers.forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

async function tryReadBody(
  body: ReadableStream<Uint8Array> | null,
): Promise<string> {
  if (!body) return "(empty)";
  try {
    const reader = body.getReader();
    const chunks: Uint8Array[] = [];
    let totalSize = 0;
    const maxSize = 10_000; // 10KB limit

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      totalSize += value.byteLength;
      if (totalSize > maxSize) {
        chunks.push(value.slice(0, maxSize - (totalSize - value.byteLength)));
        reader.cancel();
        return new TextDecoder().decode(
          concatUint8Arrays(chunks),
        ) + "... [truncated]";
      }
      chunks.push(value);
    }
    return new TextDecoder().decode(concatUint8Arrays(chunks)) || "(empty)";
  } catch {
    return "(unreadable)";
  }
}

function concatUint8Arrays(arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((sum, a) => sum + a.byteLength, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.byteLength;
  }
  return result;
}

function setupFetchInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const method = init?.method ?? "GET";
    const url =
      input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.href
          : String(input);

    let requestHeaders: Record<string, string> = {};
    if (init?.headers) {
      if (init.headers instanceof Headers) {
        requestHeaders = headersToRecord(init.headers);
      } else if (Array.isArray(init.headers)) {
        for (const [key, value] of init.headers) {
          requestHeaders[key] = value;
        }
      } else {
        requestHeaders = { ...(init.headers as Record<string, string>) };
      }
    }

    let requestBody = "(empty)";
    if (init?.body) {
      if (typeof init.body === "string") {
        requestBody =
          init.body.length > 5000
            ? init.body.slice(0, 5000) + "... [truncated]"
            : init.body;
      } else {
        requestBody = "(binary or stream)";
      }
    }

    let response: Response;
    try {
      response = await originalFetch.call(window, input, init);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err);
      addError({
        type: "network",
        timestamp: Date.now(),
        summary: `${method} ${url} — Network Error: ${message}`,
        fullReport: [
          `# Network Error (fetch threw)`,
          ``,
          `**Method:** ${method}`,
          `**URL:** ${url}`,
          `**Error:** ${message}`,
          ``,
          `## Request Headers`,
          "```json",
          JSON.stringify(redactHeaders(requestHeaders), null, 2),
          "```",
          ``,
          `## Request Body`,
          "```",
          requestBody,
          "```",
          err instanceof Error && err.stack
            ? `\n## Stack\n\`\`\`\n${err.stack}\n\`\`\``
            : "",
        ].join("\n"),
      });
      throw err;
    }

    // The exemptions table arrives with the monthly-plan migration.
    // A 404 before that migration is expected and is not a product error.
    const missingExemptionTable =
      response.status === 404 && url.includes("/billing_exemptions");
    if (response.status >= 400 && !missingExemptionTable) {
      const cloned = response.clone();
      let responseBody: string;
      try {
        responseBody = await cloned.text();
        if (responseBody.length > 5000) {
          responseBody = responseBody.slice(0, 5000) + "... [truncated]";
        }
      } catch {
        responseBody = "(unreadable)";
      }

      addError({
        type: "network",
        timestamp: Date.now(),
        summary: `${method} ${url} — ${response.status} ${response.statusText}`,
        fullReport: [
          `# HTTP Error ${response.status}`,
          ``,
          `**Method:** ${method}`,
          `**URL:** ${url}`,
          `**Status:** ${response.status} ${response.statusText}`,
          ``,
          `## Request Headers`,
          "```json",
          JSON.stringify(redactHeaders(requestHeaders), null, 2),
          "```",
          ``,
          `## Request Body`,
          "```",
          requestBody,
          "```",
          ``,
          `## Response Headers`,
          "```json",
          JSON.stringify(
            redactHeaders(headersToRecord(response.headers)),
            null,
            2,
          ),
          "```",
          ``,
          `## Response Body`,
          "```",
          responseBody,
          "```",
        ].join("\n"),
      });
    }

    return response;
  };
}

function setupConsoleInterceptors() {
  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = function patchedError(...args: unknown[]) {
    originalError.apply(console, args);
    const serialized = serializeArgs(args);
    const summary =
      serialized.length > 120 ? serialized.slice(0, 120) + "..." : serialized;
    addError({
      type: "console.error",
      timestamp: Date.now(),
      summary,
      fullReport: [
        `# Console Error`,
        ``,
        `**Arguments (${args.length}):**`,
        "```",
        serialized,
        "```",
      ].join("\n"),
    });
  };

  console.warn = function patchedWarn(...args: unknown[]) {
    originalWarn.apply(console, args);
    const serialized = serializeArgs(args);
    const summary =
      serialized.length > 120 ? serialized.slice(0, 120) + "..." : serialized;
    addError({
      type: "console.warn",
      timestamp: Date.now(),
      summary,
      fullReport: [
        `# Console Warning`,
        ``,
        `**Arguments (${args.length}):**`,
        "```",
        serialized,
        "```",
      ].join("\n"),
    });
  };
}

function setupGlobalHandlers() {
  window.addEventListener("error", (event) => {
    const { message, filename, lineno, colno, error } = event;
    // Cross-origin scripts without CORS headers get redacted to a bare
    // "Script error." with no location. These carry no actionable info
    // (most commonly injected by iOS Safari in PWA/Shortcuts contexts).
    if (message === "Script error." && !filename && !lineno && !colno) {
      return;
    }
    // ResizeObserver fires this benign error when its callback triggers
    // another size change in the same frame. Browsers handle it gracefully
    // and it's not actionable — drop it before it reaches the in-app log.
    if (
      typeof message === "string" &&
      (message.includes(
        "ResizeObserver loop completed with undelivered notifications",
      ) ||
        message.includes("ResizeObserver loop limit exceeded"))
    ) {
      return;
    }
    const stack = error?.stack ?? "(no stack)";
    addError({
      type: "global-error",
      timestamp: Date.now(),
      summary: `${message} at ${filename}:${lineno}:${colno}`,
      fullReport: [
        `# Uncaught Error`,
        ``,
        `**Message:** ${message}`,
        `**Source:** ${filename}:${lineno}:${colno}`,
        ``,
        `## Stack`,
        "```",
        stack,
        "```",
      ].join("\n"),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    let message: string;
    let stack = "(no stack)";

    if (reason instanceof Error) {
      message = `${reason.name}: ${reason.message}`;
      stack = reason.stack ?? stack;
    } else {
      try {
        message = safeStringify(reason);
      } catch {
        message = String(reason);
      }
    }

    const summary =
      message.length > 120 ? message.slice(0, 120) + "..." : message;
    addError({
      type: "unhandled-rejection",
      timestamp: Date.now(),
      summary: `Unhandled rejection: ${summary}`,
      fullReport: [
        `# Unhandled Promise Rejection`,
        ``,
        `**Reason:** ${message}`,
        ``,
        `## Stack`,
        "```",
        stack,
        "```",
      ].join("\n"),
    });
  });

  document.addEventListener("securitypolicyviolation", (event) => {
    addError({
      type: "global-error",
      timestamp: Date.now(),
      summary: `CSP violation: ${event.violatedDirective} — ${event.blockedURI}`,
      fullReport: [
        `# Content Security Policy Violation`,
        ``,
        `**Violated Directive:** ${event.violatedDirective}`,
        `**Blocked URI:** ${event.blockedURI}`,
        `**Document URI:** ${event.documentURI}`,
        `**Original Policy:** ${event.originalPolicy}`,
        `**Disposition:** ${event.disposition}`,
        `**Source File:** ${event.sourceFile || "(unknown)"}`,
        event.lineNumber ? `**Line:** ${event.lineNumber}` : "",
        event.columnNumber ? `**Column:** ${event.columnNumber}` : "",
      ]
        .filter(Boolean)
        .join("\n"),
    });
  });
}

export function setupErrorInterceptor() {
  if (initialized) return;
  initialized = true;
  // Clear any errors persisted from a previous session so the count resets on refresh.
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
  setupFetchInterceptor();
  setupConsoleInterceptors();
  setupGlobalHandlers();
}

export function getErrors(): ErrorEntry[] {
  return [...errors];
}

export function clearErrors() {
  errors = [];
  saveToStorage();
  notify();
}

export function subscribeToErrors(callback: Subscriber): () => void {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter((s) => s !== callback);
  };
}

/**
 * Manually record a network error that bypassed the fetch interceptor.
 * Used by the Sentry transport wrapper to capture failures even when
 * Sentry's internal fetch reference pre-dates our window.fetch patch.
 */
export function captureNetworkError(summary: string, fullReport: string): void {
  addError({
    type: "network",
    timestamp: Date.now(),
    summary,
    fullReport,
  });
}

// Mark tryReadBody as exported for potential future use, suppress unused warning
void tryReadBody;
